import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import AuthService from '@/services/auth.service'
import { resolveCurrentSelection } from '@/helpers/session-defaults'
import { useSessionStore } from '@/stores/session'
import { rememberFirstName, forgetFirstName } from '@/helpers/returning-user'

/**
 * Auth store for app-web authentication state.
 */
export const useAuthStore = defineStore('auth', () => {
    const user = ref(null)
    const isLoggedIn = ref(false)
    const isHydrated = ref(false)
    let hydrationPromise = null

    const setCurrentSelection = (data) => {
        data.current = {
            ...resolveCurrentSelection(data),
            flags: [],
        }
    }

    const setAuthenticatedData = (data) => {
        user.value = data
        isLoggedIn.value = true
        rememberFirstName(data?.user?.data?.first_name)

        const sessionStore = useSessionStore()
        sessionStore.setSuccess(data)
    }

    // Getters
    const status = computed(() => ({
        loggedIn: isLoggedIn.value,
    }))

    // Actions
    /**
     * Authenticate a user and initialize session data.
     *
     * @param {object} credentials
     * @returns {Promise<object>}
     */
    async function login(credentials) {
        try {
            const response = await AuthService.store(credentials)
            const data = response.data

            setCurrentSelection(data)
            setAuthenticatedData(data)
            isHydrated.value = true

            return response
        } catch (err) {
            logout(false)
            throw err
        }
    }

    /**
     * Log out the current user and clear session data.
     *
     * @param {boolean} callApi
     * @returns {Promise<void>}
     */
    async function logout(callApi = true) {
        if (callApi) {
            try {
                await AuthService.destroy()
            } catch {
                // Continue with logout even if API call fails
            }

            // Only forget the greeting name on an explicit sign-out
            // (callApi=true) — not on automatic cleanup from an expired
            // session or a failed login attempt.
            forgetFirstName()
        }

        user.value = null
        isLoggedIn.value = false
        isHydrated.value = true

        const sessionStore = useSessionStore()
        sessionStore.setDestroy()
    }

    /**
     * Hydrate auth state from the server-side cookie session.
     *
     * @returns {Promise<boolean>}
     */
    async function hydrate() {
        if (isHydrated.value) {
            return isLoggedIn.value
        }

        if (hydrationPromise != null) {
            return hydrationPromise
        }

        hydrationPromise = AuthService.session()
            .then((response) => {
                const data = response.data

                if (data == null || data.user?.data == null) {
                    user.value = null
                    isLoggedIn.value = false

                    const sessionStore = useSessionStore()
                    sessionStore.setDestroy()

                    return false
                }

                setCurrentSelection(data)
                setAuthenticatedData(data)
                return true
            })
            .catch(() => {
                user.value = null
                isLoggedIn.value = false

                const sessionStore = useSessionStore()
                sessionStore.setDestroy()

                return false
            })
            .finally(() => {
                isHydrated.value = true
                hydrationPromise = null
            })

        return hydrationPromise
    }

    /**
     * Validate the current session.
     *
     * @returns {Promise<boolean>}
     */
    async function validate() {
        try {
            return await hydrate()
        } catch {
            return false
        }
    }

    return {
        // State
        user,
        isLoggedIn,
        isHydrated,
        data: user,

        // Getters
        status,

        // Actions
        login,
        logout,
        hydrate,
        validate,
    }
})
